import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GavelIcon from "@mui/icons-material/Gavel";

const api_url = process.env.REACT_APP_BASE_URL || "https://api.bharatdarshan.app/";

export default function FraudBlocks() {
  const [blocks, setBlocks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [manualBlockOpen, setManualBlockOpen] = useState(false);
  const [manualBlockUserId, setManualBlockUserId] = useState("");
  const [manualBlockLevel, setManualBlockLevel] = useState(2);
  const [manualBlockReason, setManualBlockReason] = useState("");

  const fetchBlocks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${api_url}api/admins/fraud-blocks?page=${page + 1}&limit=${rowsPerPage}`);
      const data = await res.json();
      if (data.success) {
        setBlocks(data.data || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error("Failed to fetch fraud blocks:", err);
    }
    setLoading(false);
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  const handleUnblock = async (sourceUserId) => {
    if (!window.confirm("Are you sure you want to unblock this user and all linked identifiers?")) return;
    try {
      const res = await fetch(`${api_url}api/admins/fraud-blocks/${sourceUserId}/unblock`, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        setSnackbar({ open: true, message: `Unblocked ${data.unblocked} records`, severity: "success" });
        fetchBlocks();
      } else {
        setSnackbar({ open: true, message: data.error || "Failed to unblock", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Network error", severity: "error" });
    }
  };

  const handleViewDetails = async (sourceUserId) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await fetch(`${api_url}api/admins/fraud-blocks/${sourceUserId}/details`);
      const data = await res.json();
      if (data.success) {
        setDetailData(data);
      }
    } catch (err) {
      console.error("Failed to fetch details:", err);
    }
    setDetailLoading(false);
  };

  const handleManualBlock = async () => {
    if (!manualBlockUserId.trim()) return;
    try {
      const res = await fetch(`${api_url}api/admins/fraud-blocks/manual-block`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: manualBlockUserId.trim(),
          blockLevel: manualBlockLevel,
          reason: manualBlockReason || "Manually blocked by admin"
        })
      });
      const data = await res.json();
      if (data.success) {
        setSnackbar({ open: true, message: "User blocked successfully", severity: "success" });
        setManualBlockOpen(false);
        setManualBlockUserId("");
        setManualBlockReason("");
        fetchBlocks();
      } else {
        setSnackbar({ open: true, message: data.message || "Failed", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Network error", severity: "error" });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: "bold" }}>
          <BlockIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Fraud Block Management
        </Typography>
        <Button variant="contained" color="error" startIcon={<GavelIcon />} onClick={() => setManualBlockOpen(true)}>
          Manual Block User
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Total Active Blocks</Typography>
              <Typography variant="h4" color="error">{total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Level 1 (7-day)</Typography>
              <Typography variant="h4" color="warning.main">{blocks.filter(b => b.blockLevel === 1).length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" variant="body2">Level 2 (Permanent)</Typography>
              <Typography variant="h4" color="error">{blocks.filter(b => b.blockLevel === 2).length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Blocks Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell><b>#</b></TableCell>
                <TableCell><b>User</b></TableCell>
                <TableCell><b>Phone</b></TableCell>
                <TableCell><b>Block Level</b></TableCell>
                <TableCell><b>Reason</b></TableCell>
                <TableCell><b>Blocked At</b></TableCell>
                <TableCell><b>Unblock At</b></TableCell>
                <TableCell><b>Identifiers</b></TableCell>
                <TableCell><b>Linked Users</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} align="center"><CircularProgress size={30} /></TableCell></TableRow>
              ) : blocks.length === 0 ? (
                <TableRow><TableCell colSpan={10} align="center">No active fraud blocks</TableCell></TableRow>
              ) : (
                blocks.map((block, idx) => (
                  <TableRow key={block.sourceUserId || idx} hover>
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {block.sourceUser?.name || "Unknown"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {block.sourceUserId}
                      </Typography>
                    </TableCell>
                    <TableCell>{block.sourceUser?.phone || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={block.blockLevel === 2 ? "PERMANENT" : "7 DAYS"}
                        color={block.blockLevel === 2 ? "error" : "warning"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {block.reason}
                    </TableCell>
                    <TableCell>{block.blockedAt ? new Date(block.blockedAt).toLocaleString("en-IN") : "-"}</TableCell>
                    <TableCell>
                      {block.unblockAt ? new Date(block.unblockAt).toLocaleString("en-IN") : "Never (Permanent)"}
                    </TableCell>
                    <TableCell>
                      {(block.identifiers || []).map((ident, i) => (
                        <Chip key={i} label={`${ident.type}: ${ident.value}`} size="small" sx={{ mr: 0.5, mb: 0.5 }} variant="outlined" />
                      ))}
                    </TableCell>
                    <TableCell>
                      {(block.linkedUsers || []).length > 0 ? (
                        block.linkedUsers.map((lu, i) => (
                          <Typography key={i} variant="caption" display="block">
                            {lu.name || lu._id} {lu.phone ? `(${lu.phone})` : ""}
                          </Typography>
                        ))
                      ) : "-"}
                    </TableCell>
                    <TableCell>
                      <Button size="small" color="info" startIcon={<VisibilityIcon />} onClick={() => handleViewDetails(block.sourceUserId)} sx={{ mr: 0.5 }}>
                        Details
                      </Button>
                      <Button size="small" color="success" startIcon={<LockOpenIcon />} onClick={() => handleUnblock(block.sourceUserId)}>
                        Unblock
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
        />
      </Paper>

      {/* Details Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Fraud Block Details</DialogTitle>
        <DialogContent>
          {detailLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress /></Box>
          ) : detailData ? (
            <>
              <Typography variant="h6" sx={{ mt: 1 }}>Block Records ({detailData.blocks?.length || 0})</Typography>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Level</TableCell>
                      <TableCell>Active</TableCell>
                      <TableCell>Blocked At</TableCell>
                      <TableCell>Unblock At</TableCell>
                      <TableCell>Unblocked By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailData.blocks || []).map((b, i) => (
                      <TableRow key={i}>
                        <TableCell><Chip label={b.type} size="small" /></TableCell>
                        <TableCell>{b.value}</TableCell>
                        <TableCell>{b.blockLevel}</TableCell>
                        <TableCell><Chip label={b.isActive ? "Active" : "Inactive"} color={b.isActive ? "error" : "default"} size="small" /></TableCell>
                        <TableCell>{new Date(b.blockedAt).toLocaleString("en-IN")}</TableCell>
                        <TableCell>{b.unblockAt ? new Date(b.unblockAt).toLocaleString("en-IN") : "Permanent"}</TableCell>
                        <TableCell>{b.unblockedBy || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6">Payment Failure History ({detailData.failures?.length || 0})</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Gateway</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Transaction ID</TableCell>
                      <TableCell>Error</TableCell>
                      <TableCell>Triggered Block</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detailData.failures || []).map((f, i) => (
                      <TableRow key={i}>
                        <TableCell>{new Date(f.createdAt).toLocaleString("en-IN")}</TableCell>
                        <TableCell>{f.service}</TableCell>
                        <TableCell>{f.gateway}</TableCell>
                        <TableCell>{f.amount}</TableCell>
                        <TableCell sx={{ maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis" }}>{f.transactionId}</TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{f.errorMessage}</TableCell>
                        <TableCell>{f.triggeredBlock ? <Chip label="Yes" color="error" size="small" /> : "No"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Typography>No data</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Manual Block Dialog */}
      <Dialog open={manualBlockOpen} onClose={() => setManualBlockOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manually Block User</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="User ID" value={manualBlockUserId}
            onChange={(e) => setManualBlockUserId(e.target.value)}
            sx={{ mt: 1, mb: 2 }} placeholder="Enter MongoDB User ID"
          />
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Block Level</InputLabel>
            <Select value={manualBlockLevel} onChange={(e) => setManualBlockLevel(e.target.value)} label="Block Level">
              <MenuItem value={1}>Level 1 - 7 Days</MenuItem>
              <MenuItem value={2}>Level 2 - Permanent</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth label="Reason" value={manualBlockReason}
            onChange={(e) => setManualBlockReason(e.target.value)}
            placeholder="Reason for blocking"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualBlockOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleManualBlock}>Block User</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
